/**
 * Zod validation schemas for Aarrearkku state (Story 19.10).
 *
 * Persisted-state schemas (`OwnedItemSchema`, `DropLogEntrySchema`,
 * `AarrearkkuHouseholdStateSchema`) match the Firestore wire shape — a
 * `Date` for timestamps. Cloud Functions reading from Firestore must
 * convert `Timestamp` → `Date` before validating with these.
 *
 * Request schemas (`AwardDropRequestSchema`, `MigrateLocalStateRequestSchema`)
 * validate untrusted callable-function input where timestamps cross JSON
 * boundaries as ISO strings.
 *
 * @see shared/src/types/aarrearkku.ts (matching TypeScript types)
 * @see docs/firestore-schema.md section "13. Aarrearkku State Subcollection"
 */

import { z } from 'zod';

/**
 * Where `flavorText` came from (Story 19.11). v1 union; the pre-prod-release
 * follow-up extends with `'static'` when hand-authored fallbacks land.
 */
export const FlavorSourceSchema = z.enum(['generated', 'placeholder']);

/**
 * Persisted OwnedItem (Firestore wire shape).
 * `flavorSource` is optional for back-compat with pre-S19.11 documents.
 */
export const OwnedItemSchema = z.object({
  itemId: z.string().min(1),
  revealed: z.boolean(),
  storyId: z.string().min(1),
  awardedAt: z.date(),
  flavorText: z.string().nullable(),
  flavorSource: FlavorSourceSchema.optional(),
});

/**
 * Persisted DropLogEntry (Firestore wire shape).
 * `flavorSource` is optional for back-compat with pre-S19.11 documents.
 */
export const DropLogEntrySchema = z.object({
  itemId: z.string().min(1),
  storyId: z.string().min(1),
  awardedAt: z.date(),
  matchedSurfaceForm: z.string(),
  flavorText: z.string().nullable(),
  flavorSource: FlavorSourceSchema.optional(),
});

/**
 * Persisted Aarrearkku state document.
 */
export const AarrearkkuHouseholdStateSchema = z.object({
  version: z.literal(1),
  owned: z.array(OwnedItemSchema),
  dropLog: z.array(DropLogEntrySchema),
  processedStoryIds: z.array(z.string().min(1)),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Cloud Function request: `aarrearkkuAwardDrop`.
 */
export const AwardDropRequestSchema = z.object({
  storyId: z.string().min(1),
  itemId: z.string().min(1),
  matchedSurfaceForm: z.string().min(1),
});

/**
 * Cloud Function request: `aarrearkkuMarkRevealed` (Story 19.11).
 */
export const MarkRevealedRequestSchema = z.object({
  itemId: z.string().min(1),
});

/**
 * Legacy localStorage OwnedItem (ISO-string timestamps).
 */
export const LegacyOwnedItemSchema = z.object({
  itemId: z.string().min(1),
  revealed: z.boolean(),
  storyId: z.string().min(1),
  awardedAt: z.string().min(1),
});

/**
 * Legacy localStorage DropLogEntry (ISO-string timestamps).
 */
export const LegacyDropLogEntrySchema = z.object({
  itemId: z.string().min(1),
  storyId: z.string().min(1),
  awardedAt: z.string().min(1),
  matchedSurfaceForm: z.string(),
});

/**
 * Cloud Function request: `aarrearkkuMigrateLocalState`.
 */
export const MigrateLocalStateRequestSchema = z.object({
  owned: z.array(LegacyOwnedItemSchema),
  dropLog: z.array(LegacyDropLogEntrySchema),
  processedStoryIds: z.array(z.string().min(1)),
  sourceVersion: z.number().int(),
});
